import {
  ButtonComponentStyles,
  ClickableStackComponentStyles,
  ImageLocalComponentStyles,
  ImageRemoteComponentStyles,
  InputDateComponentStyles,
  InputSelectBaseStyles,
  InputTextBaseStyles,
  StackComponentStyles,
  TextComponentStyles,
  TitleComponentStyles,
} from 'lib/interfaces/ui-components';
import { Logo } from 'verify/models/inquiry';

import { assertNever } from './utils/types';

/**
 * Keep in sync with app/models/inquiry/styles.rb
 */
export enum StyleKey {
  // Layout
  Height = 'height',
  Width = 'width',
  Gap = 'gap',
  Margin = 'margin',
  Padding = 'padding',
  FramePositioning = 'frame_positioning',
  Alignment = 'alignment',
  Justify = 'justify',
  Axis = 'axis',
  Translate = 'translate',
  // Text
  FontFamily = 'font_family',
  FontSize = 'font_size',
  FontWeight = 'font_weight',
  LetterSpacing = 'letter_spacing',
  LineHeight = 'line_height',
  TextColor = 'text_color',
  TextColorHighlight = 'text_color_highlight',
  // Decoration
  BackgroundColor = 'background_color',
  BorderColor = 'border_color',
  BorderRadius = 'border_radius',
  BorderWidth = 'border_width',
  ShadowColor = 'shadow_color',
  ShadowOffset = 'shadow_offset',
  ShadowRadius = 'shadow_radius',
  FillColor = 'fill_color',
  StrokeColor = 'stroke_color',
  // Container
  ChildSizes = 'child_sizes',
  // Nested styles
  ButtonPrimaryStyle = 'button_primary_style',
  ButtonSecondaryStyle = 'button_secondary_style',
  ClickableStackStyle = 'clickable_stack_style',
  HorizontalStackStyle = 'horizontal_stack_style',
  InputTextStyle = 'input_text_style',
  InputDateStyle = 'input_date_style',
  InputSelectStyle = 'input_select_style',
  ImageHostedStyle = 'image_hosted_style',
  ImageLocalStyle = 'image_local_style',
  DisclaimerStyle = 'disclaimer_style',
  LogoStyle = 'logo_style',
  TextStyle = 'text_style',
  TextTransform = 'text_transform',
  TitleStyle = 'title_style',
}

export enum StyleValueFramePositioning {
  InsideMargins = 'inside_margins',
  EdgeToEdge = 'edge_to_edge',
}
export enum StyleValueAlignment {
  Start = 'start',
  Center = 'center',
  End = 'end',
}
export enum StyleValueJustify {
  Start = 'start',
  Center = 'center',
  End = 'end',
}
export enum StyleValueAxis {
  Horizontal = 'horizontal',
  Vertical = 'vertical',
}
export type StyleValueFontFamily = string;
export enum StyleValueFontWeight {
  Light = 'light',
  Normal = 'normal',
  Medium = 'medium',
  Bold = 'bold',
  Heavy = 'heavy',
}

export enum StyleValueTextTransform {
  None = 'none',
  Capitalize = 'capitalize',
  Uppercase = 'uppercase',
  Lowercase = 'lowercase',
}

export type StyleValueColorConfig = {
  unit: 'hex';
  value: string;
};

export enum ConfigValueDirection {
  Horizontal = 'start_to_end',
  Vertical = 'top_to_bottom',
  HorizontalReverse = 'end_to_start',
}

/**
 * May diverge from StyleValueColorConfig in the future.
 */
export type StyleValueColor = StyleValueColorConfig;

export type StyleValueSizeConfig =
  | {
      unit: 'px';
      value: number;
    }
  | {
      unit: '%';
      value: number;
    };
/**
 * May diverge from StyleValueSizeConfig in the future.
 */
export type StyleValueSize = StyleValueSizeConfig;

export type StyleValueBoxDimensionsConfig = {
  top: StyleValueSizeConfig | null;
  bottom: StyleValueSizeConfig | null;
  left: StyleValueSizeConfig | null;
  right: StyleValueSizeConfig | null;
};
export type StyleValueBoxDimensions = {
  top: StyleValueSize | null;
  bottom: StyleValueSize | null;
  left: StyleValueSize | null;
  right: StyleValueSize | null;
};

export type StyleValueXYDimensionsConfig = {
  x: StyleValueSizeConfig | null;
  y: StyleValueSizeConfig | null;
};
export type StyleValueXYDimensions = {
  x: StyleValueSize | null;
  y: StyleValueSize | null;
};

export type StyleValue =
  | StyleValueColor
  | StyleValueSize
  | StyleValueJustify
  | StyleValueFontFamily
  | StyleValueFontWeight
  | StyleValueBoxDimensions
  | StyleValueTextTransform
  | StyleValueXYDimensions;

export type StyleKeyToValueType = {
  // Layout
  [StyleKey.Height]: StyleValueSize;
  [StyleKey.Width]: StyleValueSize;
  [StyleKey.FramePositioning]: StyleValueFramePositioning;
  [StyleKey.Gap]: StyleValueSize;
  [StyleKey.Margin]: StyleValueBoxDimensions;
  [StyleKey.Padding]: StyleValueBoxDimensions;
  [StyleKey.Alignment]: StyleValueAlignment;
  [StyleKey.Justify]: StyleValueJustify;
  [StyleKey.Axis]: StyleValueAxis;
  [StyleKey.Translate]: StyleValueXYDimensions;
  // Text
  [StyleKey.FontFamily]: StyleValueFontFamily;
  [StyleKey.FontSize]: StyleValueSize;
  [StyleKey.FontWeight]: StyleValueFontWeight;
  [StyleKey.LetterSpacing]: StyleValueSize;
  [StyleKey.LineHeight]: StyleValueSize;
  [StyleKey.TextColor]: StyleValueColor;
  [StyleKey.TextColorHighlight]: StyleValueColor;
  [StyleKey.TextTransform]: StyleValueTextTransform;
  // Decoration
  [StyleKey.BackgroundColor]: StyleValueColor;
  [StyleKey.BorderColor]: StyleValueColor;
  [StyleKey.BorderRadius]: StyleValueSize;
  [StyleKey.BorderWidth]: StyleValueBoxDimensions;
  [StyleKey.ShadowColor]: StyleValueColor;
  [StyleKey.ShadowOffset]: StyleValueXYDimensions;
  [StyleKey.ShadowRadius]: StyleValueSize;
  [StyleKey.StrokeColor]: StyleValueColor;
  [StyleKey.FillColor]: StyleValueColor;
  // Container
  [StyleKey.ChildSizes]: number[];
  // Nested styles
  [StyleKey.ButtonPrimaryStyle]: ButtonComponentStyles;
  [StyleKey.ButtonSecondaryStyle]: ButtonComponentStyles;
  [StyleKey.ClickableStackStyle]: ClickableStackComponentStyles;
  [StyleKey.HorizontalStackStyle]: StackComponentStyles;
  [StyleKey.InputTextStyle]: InputTextBaseStyles;
  [StyleKey.InputDateStyle]: InputDateComponentStyles;
  [StyleKey.ImageHostedStyle]: ImageRemoteComponentStyles;
  [StyleKey.ImageLocalStyle]: ImageLocalComponentStyles;
  [StyleKey.DisclaimerStyle]: TextComponentStyles;
  [StyleKey.TitleStyle]: TitleComponentStyles;
  [StyleKey.TextStyle]: TextComponentStyles;
  [StyleKey.InputSelectStyle]: InputSelectBaseStyles;
  [StyleKey.LogoStyle]: Logo;
};

export enum State {
  Base = 'base',
  Focused = 'focused',
  Active = 'active',
  Disabled = 'disabled',
  Errored = 'errored',
}

export type AllStates = State.Base | State.Focused | State.Active | State.Disabled | State.Errored;
export type FormControlStates = State.Base | State.Focused | State.Disabled | State.Errored;
export type ButtonStates = State.Base | State.Focused | State.Active | State.Disabled;

export type Style<
  Attribute extends StyleKey,
  Elements extends string = 'base',
  States extends State = State.Base,
> = {
  [Element in Elements]: {
    // All styles are nullable by default, but some styles will never be null due to having a default.
    [State in States]: StyleKeyToValueType[Attribute] | undefined;
  };
};

const FONT_WEIGHT_TO_CSS: Readonly<Record<StyleValueFontWeight, number>> = {
  [StyleValueFontWeight.Light]: 300,
  [StyleValueFontWeight.Normal]: 400,
  [StyleValueFontWeight.Medium]: 500,
  [StyleValueFontWeight.Bold]: 700,
  [StyleValueFontWeight.Heavy]: 900,
};

export const fontWeightCss = (fontWeight?: StyleValueFontWeight | undefined): number | undefined =>
  fontWeight != null ? FONT_WEIGHT_TO_CSS[fontWeight] : undefined;

export const sizeCss = (size?: StyleValueSize | null): string | undefined => {
  if (size == null) {
    return undefined;
  }
  switch (size.unit) {
    case 'px':
    case '%':
      return `${size.value}${size.unit}`;
    default:
      assertNever(size);
  }
};

export const colorCss = (color?: StyleValueColor | undefined): string | undefined => {
  if (color == null) {
    return undefined;
  }
  // Change to exhaustive switch once more units are added
  return color.value;
};

export const xyCss = (xy?: StyleValueXYDimensions | undefined): string | undefined => {
  if (xy == null) {
    return undefined;
  }

  return (Object.keys(xy) as Array<keyof StyleValueXYDimensions>)
    .map((coor: keyof StyleValueXYDimensions) => {
      if (xy[coor] == null) {
        return '0px';
      }
      return sizeCss(xy[coor] as StyleValueSize) ?? `0px`;
    })
    .join(' ');
};

export const boxCss = (box?: StyleValueBoxDimensions | undefined): string | undefined => {
  if (box == null) {
    return undefined;
  }

  // ordering by box model (top, right, bottom, left)
  const boxModelOrder: Array<keyof StyleValueBoxDimensions> = ['top', 'right', 'bottom', 'left'];

  return boxModelOrder
    .map((side: keyof StyleValueBoxDimensions) => {
      if (box[side] == null) {
        return '0px';
      }
      return sizeCss(box[side] as StyleValueSize) ?? `0px`;
    })
    .join(' ');
};
